import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHeader, Spinner } from "@/components/Common";
import { TerminalSquare, RefreshCw, ShieldCheck, Server, Repeat } from "lucide-react";
import { toast } from "sonner";

const LEVEL_COLOR = { info: "text-gray-300", warn: "text-amber-400", error: "text-red-400" };

export default function ScraperLogs() {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [loading, setLoading] = useState(true);
  const termRef = useRef();

  const load = async () => {
    const [l, s] = await Promise.all([api.get("/scraper/logs?limit=150"), api.get("/scraper/status")]);
    setLogs(l.data); setStatus(s.data);
    setLoading(false);
  };
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, []);
  useEffect(() => { if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight; }, [logs]);

  const run = async () => {
    setScraping(true);
    try { await api.post("/scraper/run?limit=12"); toast.success("Scraper avviato"); setTimeout(load, 3000); }
    finally { setTimeout(() => setScraping(false), 4000); }
  };

  const st = status?.last_stats || {};

  return (
    <div className="p-6 max-w-[1500px]">
      <PageHeader title="Modulo Scraper & Anti-Bot Status" subtitle="Log live del sistema di raccolta email automatizzato (ogni 2 ore)" icon={TerminalSquare}
        actions={
          <button data-testid="scraper-run-btn" onClick={run} disabled={scraping}
            className="btn-copper px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${scraping ? "spin-slow" : ""}`} /> {scraping ? "In esecuzione..." : "Esegui ora"}
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {[
          ["Fonti scansionate", st.sites_scanned ?? 0, Server, "text-sky-400"],
          ["Richieste HTTP", st.requests ?? 0, Repeat, "text-amber-400"],
          ["Bypass anti-bot", st.bypassed ?? 0, ShieldCheck, "text-emerald-400"],
          ["User-Agent ruotati", st.user_agents_rotated ?? 0, RefreshCw, "text-purple-400"],
        ].map(([l, v, Icon, c]) => (
          <div key={l} className="card p-4">
            <Icon className={`w-5 h-5 ${c}`} />
            <div className="text-2xl font-head font-bold text-gray-100 mt-2">{v}</div>
            <div className="text-xs text-gray-500">{l}</div>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 bg-[#0E1015]">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/70" />
            <span className="w-3 h-3 rounded-full bg-amber-500/70" />
            <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
          </div>
          <span className="text-xs text-gray-500 font-mono ml-2">kuechenchef-scraper — live log</span>
        </div>
        <div ref={termRef} data-testid="scraper-terminal"
          className="p-4 font-mono text-xs leading-relaxed h-[440px] overflow-y-auto bg-[#080A0D] space-y-0.5">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : logs.length === 0 ? (
            <div className="text-gray-600">In attesa del primo ciclo di scraping...</div>
          ) : logs.map((l, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-gray-700 shrink-0">{new Date(l.timestamp).toLocaleTimeString("it-IT")}</span>
              <span className={LEVEL_COLOR[l.level] || "text-gray-300"}>{l.message}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="text-xs text-gray-500 mt-3">
        Scraping etico: solo pagine pubbliche (Impressum/Kontakt/Karriere) obbligatorie per legge in Germania, con rate-limiting e rotazione User-Agent.
      </div>
    </div>
  );
}
